import type {
  Assignment,
  Chapter,
  ChapterDetail,
  CheatItem,
  Reading,
} from './types';

const TOKEN_KEY = 'nlp_admin_token';

export const getAdminToken = () => localStorage.getItem(TOKEN_KEY) ?? '';
export const setAdminToken = (t: string) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

const authHeaders = (): Record<string, string> => {
  const t = getAdminToken();
  return t ? { authorization: `Bearer ${t}` } : {};
};

const json = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
};

export const api = {
  listChapters: () => fetch('/api/chapters').then(json<Chapter[]>),
  getChapter: (id: number) =>
    fetch(`/api/chapters/${id}`).then(json<ChapterDetail>),
  createChapter: (body: Partial<Chapter>) =>
    fetch('/api/chapters', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }).then(json<Chapter>),
  updateChapter: (id: number, body: Partial<Chapter>) =>
    fetch(`/api/chapters/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }).then(json<Chapter>),
  deleteChapter: (id: number) =>
    fetch(`/api/chapters/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(json),

  uploadReading: (form: FormData) =>
    fetch('/api/readings', {
      method: 'POST',
      headers: authHeaders(),
      body: form,
    }).then(json<Reading>),
  updateReading: (id: number, body: Partial<Reading>) =>
    fetch(`/api/readings/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }).then(json<Reading>),
  deleteReading: (id: number) =>
    fetch(`/api/readings/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(json),
  readingFileUrl: (id: number) => `/api/readings/${id}/file`,

  listAssignments: () => fetch('/api/assignments').then(json<Assignment[]>),
  createAssignment: (body: Partial<Assignment>) =>
    fetch('/api/assignments', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }).then(json<Assignment>),
  updateAssignment: (id: number, body: Partial<Assignment>) =>
    fetch(`/api/assignments/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }).then(json<Assignment>),
  deleteAssignment: (id: number) =>
    fetch(`/api/assignments/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(json),

  listCheatsheet: () => fetch('/api/cheatsheet').then(json<CheatItem[]>),
  createCheat: (body: Partial<CheatItem>) =>
    fetch('/api/cheatsheet', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }).then(json<CheatItem>),
  updateCheat: (id: number, body: Partial<CheatItem>) =>
    fetch(`/api/cheatsheet/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }).then(json<CheatItem>),
  deleteCheat: (id: number) =>
    fetch(`/api/cheatsheet/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(json),
};
